export type ProductsTabKey =
  | "Accommodation"
  | "Courses"
  | "AddOns"
  | "Transfers"
  | "Fees"
  | "Packages";

export interface OpenProductNavigationIntent {
  productId: number;
  productTab: ProductsTabKey;
}

const STORAGE_KEY = "products.openProductNavigation";

const PRICELIST_TYPE_TO_PRODUCTS_TAB: Record<string, ProductsTabKey> = {
  Accommodation: "Accommodation",
  AccommodationRoom: "Accommodation",
  Course: "Courses",
  AddOn: "AddOns",
  Transfer: "Transfers",
  Fee: "Fees",
  Package: "Packages",
};

const VALID_PRODUCT_TABS = new Set<ProductsTabKey>(
  Object.values(PRICELIST_TYPE_TO_PRODUCTS_TAB)
);

let pendingIntent: OpenProductNavigationIntent | null = null;
let hasBootstrappedFromStorage = false;

export function getPendingOpenProductIntent(): OpenProductNavigationIntent | null {
  return pendingIntent;
}

export function mapPricelistTypeToProductsTab(
  pricelistProductType: string
): ProductsTabKey | null {
  return PRICELIST_TYPE_TO_PRODUCTS_TAB[pricelistProductType] ?? null;
}

export function openProductsPageWithProduct(
  productId: number,
  pricelistProductType: string
): void {
  const productTab = mapPricelistTypeToProductsTab(pricelistProductType);
  if (!productTab || !Number.isFinite(productId) || productId < 1) {
    return;
  }

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ productId, productTab } satisfies OpenProductNavigationIntent)
    );
  } catch {
    return;
  }

  window.open("/products", "_blank", "noopener,noreferrer");
}

export function bootstrapOpenProductNavigation(): OpenProductNavigationIntent | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (hasBootstrappedFromStorage) {
    return pendingIntent;
  }
  hasBootstrappedFromStorage = true;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    localStorage.removeItem(STORAGE_KEY);

    const parsed = JSON.parse(raw) as Partial<OpenProductNavigationIntent>;
    const productId = Number(parsed.productId);
    const productTab = parsed.productTab;

    if (!productTab || !VALID_PRODUCT_TABS.has(productTab)) {
      return null;
    }

    if (!Number.isFinite(productId) || productId < 1) {
      return null;
    }

    pendingIntent = { productId, productTab };
    return pendingIntent;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function takePendingOpenProductId(
  productTab: ProductsTabKey
): number | null {
  if (pendingIntent?.productTab !== productTab) {
    return null;
  }

  const productId = pendingIntent.productId;
  pendingIntent = null;
  return productId;
}